import { ErrorOutline } from '@mui/icons-material';
import {
  FilledTextFieldProps,
  TextField as MuiTextField,
  InputAdornment,
  InputBaseComponentProps,
} from '@mui/material';
import React from 'react';

import { RequiredAsterisk } from '../RequiredAsterisk';

export type MobileEnterKeyHint = 'next' | 'go';

export interface IInnerLabelTextField
  extends Omit<FilledTextFieldProps, 'variant'> {
  /** Defines the enter label for virtual keyboards. */
  label: string;
  /** The id of the field. Usually in the following format of "moduleName.fieldName". */
  fieldName: string;
  /** Prefixes the input with a dollar sign icon. */
  currencyIcon?: boolean;
  /** Defines the enter label for virtual keyboards. */
  enterKeyHint?: MobileEnterKeyHint;
  /**
   * The error message to be rendered beneath the input. If present, the UI state of the input will reflect the error state (red border, error icon, etc.).
   */
  errorMessage?: string;
  /** Informative text to be rendered inside a tooltip. */
  infoTooltipText?: string;
  /** True if the user has focus'd and then blur'd the input */
  isBlurred?: boolean;
  /** Renders a red required asterisk beside the label. */
  required?: boolean;
  /** The test ID for the select element */
}

/** An MUI InnerLabelTextField component to be hooked up to React Hook Form. */
export const InnerLabelTextField = React.forwardRef<
  HTMLDivElement,
  IInnerLabelTextField
>(
  (
    {
      inputMode = 'text',
      enterKeyHint,
      errorMessage,
      currencyIcon,
      fieldName,
      label,
      isBlurred,
      required,
      ...props
    },
    ref
  ) => {
    const hasError = !!errorMessage && isBlurred;

    const inputProps: InputBaseComponentProps = {
      enterKeyHint,
      inputMode,
      'data-cy': fieldName,
    };
    return (
      <MuiTextField
        {...props}
        sx={{ my: 0.5 }}
        ref={ref}
        label={
          required ? (
            <>
              {label}
              <RequiredAsterisk />
            </>
          ) : (
            label
          )
        }
        key={fieldName}
        name={fieldName}
        id={fieldName}
        error={hasError}
        inputProps={inputProps}
        InputProps={{
          sx: (theme) => ({
            border: `1px solid ${theme.palette.custom.inputBorder}`,
            borderRadius: 1,
            bgcolor: 'white',
            fontSize: '18px',
            fontWeight: 500,

            '&.MuiFilledInput-adornedStart': {
              padding: 0,
            },

            '& .MuiFilledInput-inputAdornedStart': {
              paddingLeft: 1.5,
            },

            '&:hover:not(.Mui-disabled)': {
              bgcolor: 'white',
            },

            '&.Mui-focused': {
              bgcolor: 'white',
              border: `2px solid ${theme.palette.brandBlue.dark}`,
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.25)',
            },

            '&.Mui-error': {
              borderColor: theme.palette.brandRed.dark,
            },

            /* Extend autofill background to cover full input including dollar sign adornment */
            '&:has(input:-webkit-autofill)': {
              backgroundColor: 'rgb(232, 240, 254)',
            },
            /* Keep full blue background when autofilled field is hovered */
            '&:has(input:-webkit-autofill):hover:not(.Mui-disabled)': {
              backgroundColor: 'rgb(232, 240, 254)',
            },
            /* Keep full blue background when autofilled field is focused */
            '&:has(input:-webkit-autofill).Mui-focused': {
              backgroundColor: 'rgb(232, 240, 254)',
              border: `2px solid ${theme.palette.brandBlue.dark}`,
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.25)',
            },
            /* Match inner input to parent so autofill highlight is seamless across $ and input */
            '&:has(input:-webkit-autofill) input': {
              WebkitBoxShadow: '0 0 0 100px rgb(232, 240, 254) inset',
              boxShadow: '0 0 0 100px rgb(232, 240, 254) inset',
            },
          }),
          disableUnderline: true,
          endAdornment: hasError && (
            <InputAdornment position="end">
              <ErrorOutline
                sx={{ fill: (theme) => theme.palette.brandRed.dark }}
              />
            </InputAdornment>
          ),
          startAdornment: currencyIcon && (
            <InputAdornment
              position="start"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 400,
                  color: 'brandGray.dark',
                },
              }}
            >
              $
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: true,
          sx: {
            color: 'brandGray.darker',
            fontSize: '16px',
            fontWeight: 500,

            '&.MuiInputLabel-shrink': {
              fontWeight: 600,
              color: 'brandGray.darker',
            },
          },
        }}
        helperText={hasError && errorMessage}
        fullWidth
        variant="filled"
      />
    );
  }
);
